import type {
  AiAccessOverview,
  CollectStatus, GatewaySchedules, GatewayTiming,
  AdminChannel,
  CompareChannel,
  Sensor,
  SensorDetail,
  SensorPatch,
  ProjectDocument, ProjectLog, ProjectLogWrite, ProjectPerson, ProjectRole,
  ReviewFlag,
  StrategyEvent, StrategyVerdict, RuleHistoryEntry,
  StrategyRecord,
  ProjectImage,
  SensorDoc,
  SensorDocResult,
  SensorDocsList,
  ProjectMessage,
  Alarm,
  AlarmInbox,
  AlertRule,
  AlertRuleBody,
  AlertRuleOptions,
  MailStatus,
  NotificationPref,
  NotificationRecipients,
  NotificationEscalation,
  NotificationSettings,
  OutboxEmail,
  StationAlert,
  FailedPush,
  IntakeOverview,
  PushRefusal,
  ShadowComparison,
  AdminParameter,
  AdminStation,
  AppUser,
  AuditEntry,
  AuthUser,
  ChartPin,
  CalculatedSensorWrite,
  ManualReadingRecord,
  ManualReadingRevision,
  ManualSensorRecord,
  ManualSensorWrite,
  Project,
  ProjectDetail,
  ProjectMemberIn,
  ProjectWrite,
  PairReading,
  ParameterWrite,
  CompletenessDay,
  DurationCurve,
  ExportRequest,
  IngestRun,
  Revision,
  Instrument,
  InstrumentDetail,
  IssuedPushLogin,
  RatingCurve,
  LatestReading,
  MutationResult,
  Parameter,
  PushLogin,
  SeriesResponse,
  Station,
  StationDetail,
  StationPhoto,
  StationPlan,
  StationSettings,
  StationWrite,
  UnmappedChannel,
  Vendor,
  WindRose,
} from './types'
import type { ChartFormat } from '../lib/chartFormat'
import type { CalculatedSensor } from '../lib/calculatedSensors'
import type { LabStrategy } from '../lib/alarmLab'
import type { Dashboard, DashboardBody, DashboardSummary } from '../lib/dashboards'
import type { Workbook, WorkbookBody, WorkbookSummary } from '../lib/workbooks'

/** What a new pin sends: the stored reading(s) it sits on and a note. */
export interface PinWrite { parameter: string; ts: string; ts_end?: string; note: string }

/** Same-origin: Vite proxies /api to the backend in dev, and in production the
 *  static build is served alongside it. */
const BASE = '/api'

export class ApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message)
  }
}

/** Raised when the session is missing or expired, so the UI can show the
 *  sign-in form instead of an error the user can do nothing about. */
export class NotSignedIn extends ApiError {
  constructor(message: string) {
    super(401, message)
  }
}

/** Notified when any request is refused for want of a session.
 *
 *  An event rather than something derived from query state: an expired
 *  session looks identical to a live one until something is asked of it, and
 *  the error that reveals it lingers in the last query's state afterwards.
 *  Reacting to that stale error would sign the user out again the instant
 *  they sign back in. The client is the only thing that knows a 401 actually
 *  happened, and when.
 */
type SignedOutListener = (reason: string) => void
const signedOutListeners = new Set<SignedOutListener>()

export function onSignedOut(listener: SignedOutListener): () => void {
  signedOutListeners.add(listener)
  return () => signedOutListeners.delete(listener)
}

/** A page that must stay on screen when the session lapses: a dashboard on a
 *  wall, whose last values are worth more than a login page nobody asked for. While
 *  one holds, a 401 tells it and not the app, so nothing navigates; a later answered
 *  request tells it the session is back (the reader signed in on another tab). When
 *  it lets go with the session still lapsed, the app hears of it then. */
interface SignedOutHold { listener: (reason: string | undefined) => void; reason?: string }
let hold: SignedOutHold | null = null

export function holdSignedOut(listener: (reason: string | undefined) => void): () => void {
  const mine: SignedOutHold = { listener }
  hold = mine
  return () => {
    if (hold !== mine) return
    hold = null
    if (mine.reason !== undefined) announceSignedOut(mine.reason)
  }
}

function announceSignedOut(reason: string): void {
  if (hold) { hold.reason = reason; hold.listener(reason) }
  else signedOutListeners.forEach((listener) => listener(reason))
}

/** FastAPI puts the message in `detail`; a validation failure puts a list
 *  there instead, which reads as "[object Object]" if handed straight to a
 *  toast. */
async function detail(response: Response): Promise<string> {
  try {
    const body = await response.json()
    if (typeof body.detail === 'string') return body.detail
    if (Array.isArray(body.detail)) {
      return body.detail
        .map((item: { loc?: string[]; msg?: string }) =>
          `${item.loc?.slice(1).join('.') ?? ''} ${item.msg ?? ''}`.trim())
        .join('; ')
    }
    return JSON.stringify(body)
  } catch {
    return `${response.status} ${response.statusText}`
  }
}

/** Sent on every write. The API refuses a write without it: a browser will not
 *  attach a custom header cross-origin without a preflight the API never
 *  grants, so it proves the request came from these pages. */
const CSRF = { 'X-Requested-With': 'hydro' }
/** The most a keepalive request may carry (the browser's 64 kB, with room for headers). */
const KEEPALIVE_LIMIT = 60_000

/** Every call goes through here, so a lapsed session is noticed by whichever
 *  request meets it first — a chart, an export or an admin write alike. The
 *  session itself is an HttpOnly cookie the browser sends on its own; nothing
 *  in this file can read it, which is the point. */
async function request(path: string, init: RequestInit = {}): Promise<Response> {
  const method = init.method ?? 'GET'
  const response = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      // Content-Type may still be overridden by a caller's init.headers, but
      // CSRF must not be — it goes last so nothing accidentally satisfies the
      // server's check with a header that isn't actually this one.
      // A form (an upload) sets its own multipart boundary.
      ...(init.body !== undefined && !(init.body instanceof FormData)
        ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
      ...(method === 'GET' ? {} : CSRF),
    },
  })
  if (response.status === 401) {
    const reason = await detail(response)
    announceSignedOut(reason)
    throw new NotSignedIn(reason)
  }
  if (hold?.reason !== undefined && response.ok) {
    hold.reason = undefined
    hold.listener(undefined)
  }
  if (response.status === 413) {
    // nginx refuses an oversized upload with an HTML page of its own; the api's
    // own refusal is a sentence worth showing.
    const said = await response.json().then((body) => body.detail, () => undefined)
    throw new ApiError(413, typeof said === 'string' ? said : 'The file is larger than the server accepts.')
  }
  if (!response.ok) throw new ApiError(response.status, await detail(response))
  return response
}

/** A chart key in a URL: each segment encoded, the slashes kept. */
function keyPath(key: string): string {
  return key.split('/').map(encodeURIComponent).join('/')
}

/** An upload with its progress: fetch cannot report a request body's
 *  progress, so this one goes through XMLHttpRequest, with the same CSRF
 *  header, session cookie and error handling as `request`. */
function uploadForm<T>(path: string, form: FormData, onProgress: (fraction: number) => void): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${BASE}${path}`)
    xhr.withCredentials = true
    xhr.setRequestHeader(Object.keys(CSRF)[0]!, Object.values(CSRF)[0]!)
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded / event.total)
    }
    xhr.onerror = () => reject(new ApiError(0, 'The upload was interrupted. Check the connection.'))
    xhr.onabort = () => reject(new ApiError(0, 'The upload was stopped.'))
    xhr.ontimeout = () => reject(new ApiError(0, 'The upload took too long. Try again.'))
    xhr.onload = () => {
      let body: { detail?: unknown } | null = null
      try { body = JSON.parse(xhr.responseText) } catch { /* not JSON */ }
      if (body === null && xhr.status >= 200 && xhr.status < 300) {
        reject(new ApiError(xhr.status, 'The server answered something unexpected.'))
        return
      }
      body ??= {}
      const reason = typeof body.detail === 'string' ? body.detail : `HTTP ${xhr.status}`
      if (xhr.status === 401) {
        announceSignedOut(reason)
        reject(new NotSignedIn(reason))
      } else if (xhr.status === 413) {
        reject(new ApiError(413, 'The file is larger than the server accepts.'))
      } else if (xhr.status < 200 || xhr.status >= 300) {
        reject(new ApiError(xhr.status, reason))
      } else {
        resolve(body as T)
      }
    }
    xhr.send(form)
  })
}

async function get<T>(path: string, params?: URLSearchParams): Promise<T> {
  const response = await request(params ? `${path}?${params}` : path)
  return response.json() as Promise<T>
}

export const api = {
  stations: (includeInactive = false) =>
    get<Station[]>('/stations',
      new URLSearchParams({ include_inactive: String(includeInactive) })),

  station: (code: string) => get<StationDetail>(`/stations/${code}`),

  /** A station's open alerts and the checks' last round, polled under the
   *  Explore page (the detail above is fetched once). */
  stationAlerts: (code: string) =>
    get<{ alerts: StationAlert[]; health_checked_at: string | null }>(`/stations/${code}/alerts`),

  latest: (code: string) => get<LatestReading[]>(`/stations/${code}/latest`),
  /** The newest reading of each `station|parameter`, up to 64: a dashboard's cards in one request. */
  latestPairs: (pairs: string[]) => {
    const params = new URLSearchParams()
    pairs.forEach((pair) => params.append('pair', pair))
    return get<PairReading[]>('/latest', params)
  },

  /** Chart pins, shared with everyone signed in (db/028). Listed for the
   *  time window, never the zoom, so a pin outside the zoomed view is still
   *  there after Reset zoom. */
  pins: {
    list: (code: string, from: Date, to: Date, parameters: string[]) =>
      get<ChartPin[]>(`/stations/${code}/pins`, new URLSearchParams({
        start: from.toISOString(), end: to.toISOString(), parameters: parameters.join(','),
      })),
    /** Exact (station, parameter) pairs, up to 64: one analysis chart may span stations. */
    forPairs: (pairs: string[], from: Date, to: Date) => {
      const params = new URLSearchParams({ from: from.toISOString(), to: to.toISOString() })
      pairs.forEach((pair) => params.append('pair', pair))
      return get<ChartPin[]>('/pins', params)
    },
    async create(code: string, body: PinWrite): Promise<ChartPin> {
      const response = await request(`/stations/${code}/pins`, {
        method: 'POST', body: JSON.stringify(body),
      })
      return response.json() as Promise<ChartPin>
    },
    async remove(id: number): Promise<void> {
      await request(`/pins/${id}`, { method: 'DELETE' })
    },
  },

  /** Dashboards (lib/dashboards.ts). The list has no widgets; opening one loads it. */
  dashboards: {
    list: () => get<{ dashboards: DashboardSummary[] }>('/dashboards'),
    get: (id: string) => get<{ dashboard: Dashboard }>(`/dashboards/${encodeURIComponent(id)}`),
    create: (body: DashboardBody) => mutate<{ dashboard: Dashboard }>('/dashboards', 'POST', body),
    update: (id: string, body: DashboardBody) => mutate<{ dashboard: Dashboard }>(`/dashboards/${encodeURIComponent(id)}`, 'PUT', body),
    remove: (id: string) => mutate<{ id: string }>(`/dashboards/${encodeURIComponent(id)}`, 'DELETE'),
  },
  /** Analyses (lib/workbooks.ts): the reader's own and those of their projects.
   *  The list is metadata; one analysis is read when it is opened. A save sends
   *  the version it started from, and a stale one is a 409 with a sentence. */
  workbooks: {
    list: () => get<{ workbooks: WorkbookSummary[] }>('/workbooks'),
    get: (id: string) => get<{ workbook: Workbook }>(`/workbooks/${encodeURIComponent(id)}`),
    create: (body: WorkbookBody) => mutate<{ workbook: Workbook }>('/workbooks', 'POST', body),
    update: (id: string, body: WorkbookBody) => mutate<{ workbook: Workbook }>(`/workbooks/${encodeURIComponent(id)}`, 'PUT', body),
    remove: (id: string) => mutate<{ id: string }>(`/workbooks/${encodeURIComponent(id)}`, 'DELETE'),
    favorite: (id: string, on: boolean) => on
      ? mutate<{ id: string; favorite: boolean }>(`/workbooks/${encodeURIComponent(id)}/favorite`, 'PUT')
      : mutate<{ id: string; favorite: boolean }>(`/workbooks/${encodeURIComponent(id)}/favorite`, 'DELETE'),
  },

  /** Project logs: dated notes on a project (db/043). Any member reads and
   *  adds; the author, an Editor or an Admin removes. */
  projectLogs: {
    list: (projectId?: number) =>
      get<{ logs: ProjectLog[] }>('/project-logs',
        projectId ? new URLSearchParams({ project_id: String(projectId) }) : undefined),
    add: (body: ProjectLogWrite) => mutate<{ id: number }>('/project-logs', 'POST', body),
    remove: (id: number) => mutate<{ deleted: number }>(`/project-logs/${id}`, 'DELETE'),
  },
  /** Projects (phase 3). Reads show only the reader's projects; each write
   *  is checked against the reader's role in the project on the server, and
   *  a non-member gets 404 there, as for a missing project. */
  projects: {
    list: () => get<{ projects: Project[]; health_checked_at: string | null }>('/projects'),
    get: (id: number) => get<ProjectDetail>(`/projects/${id}`),
    update: (id: number, changes: Partial<ProjectWrite>) =>
      mutate<{ project: ProjectDetail['project'] }>(`/projects/${id}`, 'PATCH', changes),
    setStations: (id: number, stations: string[]) =>
      mutate<{ project: ProjectDetail['project'] }>(`/projects/${id}/stations`, 'PUT', { stations }),
    setMembers: (id: number, members: ProjectMemberIn[]) =>
      mutate<{ project: ProjectDetail['project'] }>(`/projects/${id}/members`, 'PUT', { members }),
    archive: (id: number) =>
      mutate<{ project: ProjectDetail['project'] }>(`/projects/${id}/archive`, 'POST'),

    /** The accounts a project can be given (viewer or admin platform role):
     *  a platform admin sees all; a project Admin names their project. */
    people: (projectId?: number) =>
      get<{ people: ProjectPerson[] }>('/project-people',
        projectId ? new URLSearchParams({ project_id: String(projectId) }) : undefined),
    /** One person's role in one project, or `null` to take them out (a
     *  platform admin's; the server applies the project form's own rules). */
    setUserRole: (projectId: number, userId: number, role: ProjectRole | null) =>
      mutate<{ role: ProjectRole | null }>(`/admin/users/${userId}/projects/${projectId}`, 'PUT', { role }),

    /** Chat and images (phase 5): every member posts and uploads. */
    messages: (id: number, before?: number) =>
      get<{ messages: ProjectMessage[]; more: boolean }>(`/projects/${id}/messages`,
        before ? new URLSearchParams({ before: String(before) }) : undefined),
    postMessage: async (id: number, body: string, image?: File, station?: string) => {
      const form = new FormData()
      form.set('body', body)
      if (image) form.set('image', image)
      if (station) form.set('station', station)
      const response = await request(`/projects/${id}/messages`, { method: 'POST', body: form })
      return response.json() as Promise<{ message: ProjectMessage }>
    },
    removeMessage: (id: number, messageId: number) =>
      mutate<{ message: { id: number } }>(`/projects/${id}/messages/${messageId}`, 'DELETE'),
    images: (id: number, station?: string) =>
      get<{ images: ProjectImage[] }>(`/projects/${id}/images`,
        station ? new URLSearchParams({ station }) : undefined),
    documents: (id: number) => get<{ documents: ProjectDocument[] }>(`/projects/${id}/documents`),
    uploadDocument: (id: number, file: File, onProgress: (fraction: number) => void) => {
      const form = new FormData()
      form.append('files', file)
      return uploadForm<{ documents: ProjectDocument[]; refused: { name: string; reason: string }[] }>(
        `/projects/${id}/documents`, form, onProgress)
    },
    removeDocument: (id: number, documentId: number) =>
      mutate<{ document: { id: number } }>(`/projects/${id}/documents/${documentId}`, 'DELETE'),
    /** Sensor Docs: every file on the project's sensors, and the sensors. */
    sensorDocs: (id: number) => get<SensorDocsList>(`/projects/${id}/sensor-docs`),
    /** One file to the sensors named ("{station}/{key}"), all or none. */
    attachSensorDoc: (id: number, file: File, refs: string[],
                      onProgress: (fraction: number) => void) => {
      const form = new FormData()
      form.set('file', file)
      form.set('sensors', JSON.stringify(refs))
      return uploadForm<SensorDocResult>(`/projects/${id}/sensor-docs`, form, onProgress)
    },
    changeSensorDoc: (id: number, key: string, change: { add: string[]; remove: string[] }) =>
      mutate<{ doc: SensorDoc | null; added: number; removed: number; already: number }>(
        `/projects/${id}/sensor-docs/${encodeURIComponent(key)}`, 'PATCH', change),
  },

  /** Manual sensors (db/034): gauges read by hand, kept on the server. A
   *  project's members read them and enter readings; its Editors and
   *  Admins change the sensors. A stored reading is replaced only when the
   *  request says the reader agreed (`replace`), otherwise 409. */
  manualSensors: {
    list: (filter: { project?: number; station?: string } = {}) => {
      const params = new URLSearchParams()
      if (filter.project) params.set('project', String(filter.project))
      if (filter.station) params.set('station', filter.station)
      return get<{ sensors: ManualSensorRecord[] }>('/manual-sensors', params.size ? params : undefined)
    },
    create: (body: ManualSensorWrite) => mutate<{ sensor: ManualSensorRecord }>('/manual-sensors', 'POST', body),
    update: (key: string, patch: Partial<ManualSensorWrite>) =>
      mutate<{ sensor: ManualSensorRecord }>(`/manual-sensors/${encodeURIComponent(key)}`, 'PATCH', patch),
    remove: (key: string) => mutate<{ deleted: string }>(`/manual-sensors/${encodeURIComponent(key)}`, 'DELETE'),
    readings: (key: string, window: { from?: Date; to?: Date } = {}) => {
      const params = new URLSearchParams()
      if (window.from) params.set('start', window.from.toISOString())
      if (window.to) params.set('end', window.to.toISOString())
      return get<{ readings: ManualReadingRecord[] }>(`/manual-sensors/${encodeURIComponent(key)}/readings`,
        params.size ? params : undefined)
    },
    addReadings: (key: string, body: {
      rows: { ts: string; value: number; note: string }[]; replace: boolean
      origin: 'typed' | 'import'; import_name?: string | null
    }) => mutate<{ added: number; replaced: number; unchanged: number }>(
      `/manual-sensors/${encodeURIComponent(key)}/readings`, 'POST', body),
    revisions: (key: string) =>
      get<{ revisions: ManualReadingRevision[] }>(`/manual-sensors/${encodeURIComponent(key)}/revisions`),
    updateReading: (id: number, patch: { ts?: string; value?: number; note?: string; replace?: boolean }) =>
      mutate<{ reading: ManualReadingRecord | null; changed: boolean }>(`/manual-readings/${id}`, 'PATCH', patch),
    removeReading: (id: number) => mutate<{ deleted: number }>(`/manual-readings/${id}`, 'DELETE'),
  },

  /** Calculated sensors (db/035): the definitions only, in a project. */
  calculatedSensors: {
    list: (project?: number) => get<{ sensors: CalculatedSensor[] }>('/calculated-sensors',
      project ? new URLSearchParams({ project: String(project) }) : undefined),
    create: (body: CalculatedSensorWrite) => mutate<{ sensor: CalculatedSensor }>('/calculated-sensors', 'POST', body),
    update: (key: string, patch: Partial<CalculatedSensorWrite>) =>
      mutate<{ sensor: CalculatedSensor }>(`/calculated-sensors/${encodeURIComponent(key)}`, 'PATCH', patch),
    remove: (key: string) => mutate<{ deleted: string }>(`/calculated-sensors/${encodeURIComponent(key)}`, 'DELETE'),
  },

  /** Alarm Lab strategies as live rules (db/036). A new one is saved off;
   *  switching it on makes it live. Members read; Editors and Admins write. */
  strategies: {
    list: (project?: number) => get<{ strategies: StrategyRecord[]; live: number; max_live: number }>('/strategies',
      project ? new URLSearchParams({ project: String(project) }) : undefined),
    create: (body: { project_id: number; strategy: LabStrategy; enabled?: boolean }) =>
      mutate<{ strategy: StrategyRecord }>('/strategies', 'POST', body),
    update: (id: number, patch: { project_id?: number; strategy?: LabStrategy; enabled?: boolean }) =>
      mutate<{ strategy: StrategyRecord }>(`/strategies/${id}`, 'PATCH', patch),
    remove: (id: number) => mutate<{ deleted: number }>(`/strategies/${id}`, 'DELETE'),
    events: (id: number) => get<{ events: StrategyEvent[] }>(`/strategies/${id}/events`),
    history: (id: number) => get<{ history: RuleHistoryEntry[] }>(`/strategies/${id}/history`),
    /** What the rule monitor would make of it now, judged on demand. */
    verdict: (id: number) => get<StrategyVerdict>(`/strategies/${id}/verdict`),
  },

  /** One person's workspace documents (db/035), keyed on the session. */
  documents: {
    all: () => get<{ documents: Record<string, unknown> }>('/me/documents'),
    /** `keepalive` lets the save outlive the page it was made on; the
     *  browser allows it only for a small body (64 kB). */
    put: async (key: string, value: unknown, options: { keepalive?: boolean } = {}) => {
      const body = JSON.stringify({ value })
      const response = await request(`/me/documents/${encodeURIComponent(key)}`,
        { method: 'PUT', body, keepalive: Boolean(options.keepalive) && body.length < KEEPALIVE_LIMIT })
      return response.json() as Promise<{ key: string }>
    },
    remove: async (key: string, options: { keepalive?: boolean } = {}) => {
      await request(`/me/documents/${encodeURIComponent(key)}`, { method: 'DELETE', keepalive: Boolean(options.keepalive) })
    },
  },

  /** The Overview's review flags (db/035), shared, with who flagged each. */
  reviewFlags: {
    list: () => get<{ flags: ReviewFlag[] }>('/review-flags'),
    /** `occurrence` is the incident being flagged (lib/overviewStatus
     *  issueOccurrence); a different one replaces the flag. */
    flag: (issue: string, note: string, occurrence: string) =>
      mutate<{ flags: ReviewFlag[] }>(`/review-flags/${encodeURIComponent(issue)}`, 'PUT', { note, occurrence }),
    editNote: (issue: string, note: string) =>
      mutate<{ flags: ReviewFlag[] }>(`/review-flags/${encodeURIComponent(issue)}`, 'PATCH', { note }),
    /** Anyone signed in: who looked, when, and what they found. */
    check: (issue: string, note: string) =>
      mutate<{ flags: ReviewFlag[] }>(`/review-flags/${encodeURIComponent(issue)}/check`, 'PUT', { note }),
    unflag: (issue: string) => mutate<{ flags: ReviewFlag[] }>(`/review-flags/${encodeURIComponent(issue)}`, 'DELETE'),
  },

  /** The signed-in person's alarm inbox: the events on rules that tell them.
   *  A check with a note is an acknowledgement everyone sees; without one it
   *  only clears the person's own inbox. */
  alarmInbox: {
    list: (before?: number | null) =>
      get<AlarmInbox>('/me/alarm-inbox', before ? new URLSearchParams({ before: String(before) }) : undefined),
    check: (id: number, note?: string | null) =>
      mutate<{ checked: number }>(`/me/alarm-inbox/${id}/check`, 'POST', { note: note?.trim() || null }),
  },

  /** Alert rules and alarms (phase 4). The rules listed are those the
   *  reader manages: every one for a platform admin, those on their
   *  projects' stations for a project Editor or Admin. Anyone reads alarms. */
  alerts: {
    rules: (project?: number | null) =>
      get<{ rules: AlertRule[] }>('/alert-rules',
        project ? new URLSearchParams({ project: String(project) }) : undefined),
    stations: () => get<{ stations: { code: string; name: string; vendor: Vendor }[] }>(
      '/alert-rules/stations'),
    options: (station: string) =>
      get<AlertRuleOptions>('/alert-rules/options', new URLSearchParams({ station })),
    create: (body: AlertRuleBody) => mutate<{ rule: AlertRule }>('/alert-rules', 'POST', body),
    update: (id: number, patch: Partial<AlertRuleBody>) =>
      mutate<{ rule: AlertRule }>(`/alert-rules/${id}`, 'PATCH', patch),
    remove: (id: number) => mutate<{ rule: { id: number } }>(`/alert-rules/${id}`, 'DELETE'),
    /** Its create, change, switch and delete rows: for whoever manages the rule. */
    history: (id: number) => get<{ history: RuleHistoryEntry[] }>(`/alert-rules/${id}/history`),
    alarms: (filter: { station?: string; project?: number } = {}) => {
      const params = new URLSearchParams()
      if (filter.station) params.set('station', filter.station)
      if (filter.project) params.set('project', String(filter.project))
      return get<{ alarms: Alarm[]; checked_at: string | null }>('/alarms', params.size ? params : undefined)
    },
    mailConfigured: () => get<{ configured: boolean }>('/alerts/mail-status'),
    myPref: () => get<NotificationPref>('/me/notification-pref'),
    setMyPref: (pref: NotificationPref) =>
      mutate<NotificationPref>('/me/notification-pref', 'PUT',
        { email: pref.email, receives: pref.receives }),
  },

  /** Connected AI clients and what they asked for (backend/app/mcp); everyone's
   *  for a platform admin. */
  aiAccess: {
    get: () => get<AiAccessOverview>('/me/ai-access'),
    async disconnect(id: number): Promise<void> {
      await request(`/me/ai-access/grants/${id}`, { method: 'DELETE' })
    },
  },

  /** The reader's own formatting of one chart (`lib/chartFormat.ts`). */
  chartFormat: {
    get: (key: string) => get<{ settings: ChartFormat | null }>(`/me/chart-format/${keyPath(key)}`),
    async put(key: string, settings: ChartFormat): Promise<void> {
      await request(`/me/chart-format/${keyPath(key)}`, { method: 'PUT', body: JSON.stringify(settings) })
    },
    async reset(key: string): Promise<void> {
      await request(`/me/chart-format/${keyPath(key)}`, { method: 'DELETE' })
    },
  },

  /** Collect now (db/037): the latest request, and asking for another. */
  collectStatus: (code: string) => get<CollectStatus>(`/stations/${code}/collect`),
  collectNow: (code: string) =>
    mutate<CollectStatus>(`/stations/${code}/collect`, 'POST'),

  photos: (code: string, before?: string, limit = 12) =>
    get<StationPhoto[]>(`/stations/${code}/photos`, new URLSearchParams(
      before ? { before, limit: String(limit) } : { limit: String(limit) })),

  /** An <img> src, not a fetch: the browser sends the session cookie itself
   *  and may cache the photo, which never changes. */
  photoUrl: (code: string, id: string) =>
    `${BASE}/stations/${encodeURIComponent(code)}/photos/${encodeURIComponent(id)}`,

  instruments: (code: string) =>
    get<Instrument[]>(`/stations/${code}/instruments`),

  instrument: (code: string, id: number) =>
    get<InstrumentDetail>(`/stations/${code}/instruments/${id}`),

  parameters: () => get<Parameter[]>('/parameters'),

  series: (options: SeriesOptions) => get<SeriesResponse>('/series', seriesParams(options)),

  /** Sensors (phase 6): every gateway node and each MDS or MRL station's
   *  logger, with a status the api decides. Anyone reads; a platform admin
   *  or a project Editor or Admin on the station describes. */
  sensors: {
    list: (station?: string) =>
      get<{ sensors: Sensor[]; health_checked_at: string | null }>('/sensors',
        station ? new URLSearchParams({ station }) : undefined),
    get: (code: string, key: string) =>
      get<SensorDetail>(`/stations/${encodeURIComponent(code)}/sensors/${encodeURIComponent(key)}`),
    update: (code: string, key: string, patch: SensorPatch) =>
      mutate<SensorDetail>(
        `/stations/${encodeURIComponent(code)}/sensors/${encodeURIComponent(key)}`, 'PATCH', patch),
    upload: (code: string, key: string, kind: 'image' | 'document', file: File,
             onProgress: (fraction: number) => void) => {
      const form = new FormData()
      form.append('files', file)
      form.set('kind', kind)
      return uploadForm<{ added: number; refused: { name: string; reason: string }[] }>(
        `/stations/${encodeURIComponent(code)}/sensors/${encodeURIComponent(key)}/files`,
        form, onProgress)
    },
    removeFile: (id: number) => mutate<{ file: { id: number } }>(`/sensor-files/${id}`, 'DELETE'),
  },

  compareChannels: () => get<CompareChannel[]>('/compare/channels'),

  durationCurve: (station: string, parameter: string, from: Date, to: Date) =>
    get<DurationCurve>('/analytics/duration-curve', new URLSearchParams({
      station, parameter, from: from.toISOString(), to: to.toISOString(),
    })),

  ratingCurve: (station: string, levelParam: string, flowParam: string,
                from: Date, to: Date) =>
    get<RatingCurve>('/analytics/rating-curve', new URLSearchParams({
      station, level_param: levelParam, flow_param: flowParam,
      from: from.toISOString(), to: to.toISOString(),
    })),

  windRose: (station: string, from: Date, to: Date) =>
    get<WindRose>('/analytics/wind-rose', new URLSearchParams({
      station, from: from.toISOString(), to: to.toISOString(),
    })),

  completeness: (from: Date, to: Date, stations?: string[]) => {
    const params = new URLSearchParams({
      from: from.toISOString(), to: to.toISOString(),
    })
    stations?.forEach((s) => params.append('station', s))
    return get<CompletenessDay[]>('/analytics/completeness', params)
  },

  /** Triggers a download. The filename comes from Content-Disposition rather
   *  than being rebuilt here, so the file the user gets is the one the server
   *  says it built. */
  async export(body: ExportRequest): Promise<void> {
    const response = await request('/export', {
      method: 'POST',
      body: JSON.stringify(body),
    })

    const blob = await response.blob()
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filenameFrom(response.headers.get('Content-Disposition'))
      ?? `hydro-export.${body.format ?? 'csv'}`
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
  },
}

/** The saved name: the UTF-8 `filename*` (RFC 5987) when the server sends
 *  one, so a Georgian project name survives, else the plain `filename`. */
export function filenameFrom(header: string | null): string | undefined {
  const utf8 = header?.match(/filename\*=UTF-8''([^;]+)/i)
  if (utf8?.[1]) {
    try { return decodeURIComponent(utf8[1].trim()) } catch { /* fall through */ }
  }
  const match = header?.match(/filename="([^"]+)"/)
  return match?.[1]
}

export interface SeriesOptions {
  stations: string[]
  parameters: string[]
  from: Date
  to: Date
  includeQuality?: boolean
  /** Exact (station, parameter) pairs across stations; when given, the
   *  station and parameter lists are not sent. */
  pairs?: [string, string][]
}

/** The query string for /api/series: stations, parameters and the window as
 *  instants. No resolution and no aggregate — the API serves stored readings
 *  only and refuses either by name (2026-09-28). */
export function seriesParams(options: SeriesOptions): URLSearchParams {
  const params = new URLSearchParams()
  if (options.pairs?.length) {
    options.pairs.forEach(([station, parameter]) => params.append('pair', `${station}|${parameter}`))
  } else {
    options.stations.forEach((s) => params.append('station', s))
    options.parameters.forEach((p) => params.append('parameter', p))
  }
  params.set('from', options.from.toISOString())
  params.set('to', options.to.toISOString())
  if (options.includeQuality) params.set('include_quality', 'true')
  return params
}

// ------------------------------------------------------------------- admin

async function adminRequest<T>(path: string, method: string,
                               body?: unknown): Promise<T> {
  const response = await request(path, {
    method,
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  return response.json() as Promise<T>
}

const mutate = adminRequest

/** The signed-in user. Sign-in itself is a page navigation to
 *  /api/auth/login, never a fetch — see lib/auth.tsx `loginHref`. */
export const auth = {
  me: () => get<AuthUser>('/auth/me'),
  logout: async () => {
    await request('/auth/logout', { method: 'POST' })
  },
}

export const admin = {
  users: () => adminRequest<AppUser[]>('/admin/users', 'GET'),

  /** Alert delivery (phase 4): platform-wide settings, everyone's email
   *  choice, the outbox and mail's own state. */
  notificationSettings: () => adminRequest<NotificationSettings>('/admin/notification-settings', 'GET'),
  setNotificationSettings: (body: NotificationSettings) =>
    mutate<NotificationSettings>('/admin/notification-settings', 'PUT',
      { remind_every_h: body.remind_every_h, digest: body.digest }),
  escalation: () => adminRequest<NotificationEscalation>('/admin/notification-escalation', 'GET'),
  setEscalation: (body: NotificationEscalation) =>
    mutate<NotificationEscalation>('/admin/notification-escalation', 'PUT', body),
  recipients: () => adminRequest<NotificationRecipients>('/admin/notification-recipients', 'GET'),
  setPref: (userId: number, pref: NotificationPref) =>
    mutate<NotificationPref>(`/admin/notification-prefs/${userId}`, 'PUT',
      { email: pref.email, receives: pref.receives }),
  outbox: (status?: OutboxEmail['status']) =>
    adminRequest<{ emails: OutboxEmail[] }>(
      status ? `/admin/mail-outbox?status=${status}` : '/admin/mail-outbox', 'GET'),
  mailStatus: () => adminRequest<MailStatus>('/admin/mail-status', 'GET'),

  /** Creating a project is a platform admin's; the creator becomes its
   *  project Admin. Everything after is `api.projects`, by project role. */
  createProject: (body: ProjectWrite & { stations: string[]; members: ProjectMemberIn[] }) =>
    mutate<{ project: ProjectDetail['project'] }>('/admin/projects', 'POST', body),

  setUserRole: (id: number, role: AppUser['role']) =>
    mutate<MutationResult<never> & { user: AppUser }>(
      `/admin/users/${id}`, 'PATCH', { role }),

  // Every read below is behind the same gate as the writes — the station
  // config, the ingest log and the audit trail are not public either.
  stations: () => adminRequest<AdminStation[]>('/admin/stations', 'GET'),

  pushLogins: (code: string) =>
    adminRequest<PushLogin[]>(`/admin/stations/${code}/push-credentials`, 'GET'),

  issuePushLogin: (code: string, kind: 'basic' | 'path', loggerId?: string) =>
    adminRequest<IssuedPushLogin>(`/admin/stations/${code}/push-credentials`, 'POST',
      loggerId ? { kind, logger_id: loggerId } : { kind }),

  revokePushLogin: (id: number) =>
    adminRequest<{ id: number; revoked: boolean }>(`/admin/push-credentials/${id}`, 'DELETE'),

  /** Gateway rounds (db/037): timing learned and overridden per gateway. */
  gatewaySchedules: () => adminRequest<GatewaySchedules>('/admin/gateway-schedules', 'GET'),
  updateGatewaySchedule: (code: string, change: Partial<GatewayTiming>) =>
    mutate<Partial<GatewayTiming> & { code: string }>(
      `/admin/gateway-schedules/${code}`, 'PATCH', change),

  /** The Intake page (intake v2 phase 5). */
  intake: () => adminRequest<IntakeOverview>('/admin/intake', 'GET'),
  failedPushes: (limit = 100) =>
    adminRequest<FailedPush[]>(`/admin/intake/pushes?limit=${limit}`, 'GET'),
  reparsePush: (id: number) =>
    mutate<{ id: number; warnings: string[] }>(`/admin/intake/pushes/${id}/reparse`, 'POST'),
  refusals: (limit = 100) =>
    adminRequest<PushRefusal[]>(`/admin/intake/refusals?limit=${limit}`, 'GET'),
  shadow: (code: string, days = 7) =>
    adminRequest<ShadowComparison>(`/admin/intake/shadow/${code}?days=${days}`, 'GET'),

  /** The editable row. The list serves `station_health`, which omits the
   *  retry settings and coordinates. */
  settings: (code: string) =>
    adminRequest<StationSettings>(`/admin/stations/${code}/settings`, 'GET'),

  channels: (code: string) =>
    adminRequest<AdminChannel[]>(`/admin/stations/${code}/channels`, 'GET'),

  unmapped: (includeResolved = false) =>
    adminRequest<UnmappedChannel[]>(
      `/admin/unmapped?include_resolved=${includeResolved}`, 'GET'),

  /** The collection run log. `trigger` narrows it to one kind — without it a
   *  monthly audit scrolls out of the list within weeks. */
  runs: (options: { limit?: number; station?: string; trigger?: string } = {}) => {
    const params = new URLSearchParams({ limit: String(options.limit ?? 100) })
    if (options.station) params.set('station', options.station)
    if (options.trigger) params.set('trigger', options.trigger)
    return adminRequest<IngestRun[]>(`/admin/runs?${params}`, 'GET')
  },

  /** Vendor readings that disagree with what is stored (db/016). */
  revisions: (options: { limit?: number; station?: string;
                         outcome?: Revision['outcome'] } = {}) => {
    const params = new URLSearchParams({ limit: String(options.limit ?? 200) })
    if (options.station) params.set('station', options.station)
    if (options.outcome) params.set('outcome', options.outcome)
    return adminRequest<Revision[]>(`/admin/revisions?${params}`, 'GET')
  },

  audit: (limit = 100) =>
    adminRequest<AuditEntry[]>(`/admin/audit?limit=${limit}`, 'GET'),

  // A channel can only be mapped to a parameter that already exists, so a
  // station reporting something new needs one defined first.
  parameters: () => adminRequest<AdminParameter[]>('/admin/parameters', 'GET'),

  createParameter: (body: ParameterWrite) =>
    adminRequest<MutationResult<never> & { parameter: AdminParameter }>(
      '/admin/parameters', 'POST', body),

  updateParameter: (code: string, body: ParameterWrite) =>
    adminRequest<MutationResult<never> & { parameter: AdminParameter }>(
      `/admin/parameters/${code}`, 'PATCH', body),

  deleteParameter: (code: string) =>
    adminRequest<MutationResult<never>>(`/admin/parameters/${code}`, 'DELETE'),

  createStation: (body: StationWrite) =>
    mutate<MutationResult<AdminStation>>('/admin/stations', 'POST', body),

  /** Read-only. Asks MDS what the station is and derives the rest. */
  probeStation: (name: string, externalId: string, vendor: Vendor) =>
    mutate<StationPlan>('/admin/stations/probe', 'POST',
      { name, external_id: externalId, vendor }),

  provisionStation: (plan: StationPlan) =>
    mutate<MutationResult<AdminStation>>('/admin/stations/provision', 'POST',
      plan),

  updateStation: (code: string, body: StationWrite) =>
    mutate<MutationResult<AdminStation>>(`/admin/stations/${code}`, 'PATCH',
      body),

  deactivateStation: (code: string) =>
    mutate<MutationResult<AdminStation>>(`/admin/stations/${code}`, 'DELETE'),

  reactivateStation: (code: string) =>
    mutate<MutationResult<AdminStation>>(`/admin/stations/${code}/reactivate`,
      'POST'),

  mapChannel: (code: string, channelId: number, body: {
    parameter: string
    event_driven?: boolean
    min_valid?: number | null
    max_valid?: number | null
    enabled?: boolean
  }) =>
    mutate<MutationResult<never>>(
      `/admin/stations/${code}/channels/${channelId}`, 'PUT', body),

  disableChannel: (code: string, channelId: number) =>
    mutate<MutationResult<never>>(
      `/admin/stations/${code}/channels/${channelId}`, 'DELETE'),

  resolveUnmapped: (id: number, body: {
    action: 'map' | 'dismiss'
    parameter?: string
    event_driven?: boolean
    note?: string
  }) =>
    mutate<MutationResult<never>>(`/admin/unmapped/${id}/resolve`, 'POST',
      body),
}
